import type { FormInstance } from 'antd';
import { Form } from 'antd';
import classNames from 'classnames';
import type { FieldData } from 'rc-field-form/es/interface';
import React, { useCallback, useContext, useEffect } from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useHotelPacks } from 'app/hooks/data/core/extranet/hotel/useHotelPacks';
import { mapToPackResponse } from 'app/hooks/data/core/extranet/inventory/mapping/pack';
import { useSavePacks } from 'app/hooks/data/core/extranet/inventory/packs/usePacks';
import { usePacksScores } from 'app/hooks/data/core/extranet/inventory/packs/usePacksScores';

import type {
  InventoryExperiences,
  InventoryPacks,
  InventoryRooms,
} from '../../../InventoryStore';
import {
  useInventoryForm,
  useInventoryState,
  useTabInventoryActions,
} from '../../../hooks';
import { usePackInventoryState } from '../../../hooks/usePackInventorySelectors';
import {
  getChangedFormFields,
  getModifiedValues,
  mergeInventoryObjects,
  retrieveDataFromNamePath,
  retrieveFormValues,
} from '../../../utils';
import { updateMaxBookingForDate } from '../../../utils/inventoryUpdates';
import { EmptyState } from '../../EmptyState/EmptyState';
import { PackTabSkeleton } from '../../Skeletons/PackTabSkeleton/PackTabSkeleton';
import { isEnabledByMessage } from '../tabViewUtils';

import styles from './PacksTab.module.scss';
import { DayPackTableItem } from './_components/DayPackTableItem';
import { PackTableItem } from './_components/PackTableItem';
import { useFilteredPacks } from './hooks/useFilteredPacks';

type PackViewProps = {
  isDaytime: boolean;
};

export const PackView: React.FC<PackViewProps> = ({ isDaytime }) => {
  const [form] = Form.useForm();

  const { hotelInfo } = useContext(HotelContext);

  const { timePeriod } = useInventoryState();
  const { isLoading: isLoadingPacks } = useHotelPacks(hotelInfo?.id);
  const { data: packsScores } = usePacksScores(hotelInfo?.id);

  const {
    inventoryPacks,
    inventory,
    dependencies,
    rawInventory,
    modifiedInventory,
    timePeriodStore,
  } = usePackInventoryState();

  const { setForm, setModifiedInventory } = useTabInventoryActions();
  const { mutateAsync: savePacksInventory } = useSavePacks(
    hotelInfo?.id,
    timePeriod
  );

  const storeForm = useInventoryForm();

  useEffect(() => {
    setForm(
      form as FormInstance<
        InventoryExperiences | InventoryRooms | InventoryPacks
      >
    );
  }, [form, setForm]);

  useEffect(() => {
    const mergedInventory = mergeInventoryObjects(
      inventory ?? {},
      timePeriodStore
    );

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const inventoryPacks = retrieveFormValues(mergedInventory).inventoryPacks;

    form.setFieldsValue(inventoryPacks);

    //We don't want this to re-run on every form change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy directive, needs investigation
  }, [inventoryPacks]);

  const { sortedNightStayPacks, sortedDaytimePacks } = useFilteredPacks();
  const sortedPacks = isDaytime ? sortedDaytimePacks : sortedNightStayPacks;

  const onFinish = async (values: InventoryPacks) => {
    const { inventoryPacks: packTimePeriodStore } =
      retrieveFormValues(timePeriodStore);

    const modifiedValues = getModifiedValues(
      { ...values, ...packTimePeriodStore },
      inventoryPacks
    );

    await savePacksInventory(mapToPackResponse(modifiedValues));
  };

  const handleFieldsChange = useCallback(
    (changedFields: FieldData[]) => {
      if (!rawInventory || !dependencies) {
        return;
      }

      const retrieved = retrieveDataFromNamePath(changedFields[0]?.name);

      if (!retrieved) {
        return;
      }

      const { dateKey, itemId } = retrieved;

      const existing = modifiedInventory?.[dateKey]?.packs?.[Number(itemId)];

      if (!existing) {
        return;
      }

      if (!isEnabledByMessage(existing.message)) {
        return;
      }

      let currentInventory = modifiedInventory ?? {};

      const prevEntry = currentInventory[dateKey]?.packs?.[Number(itemId)] as
        | Record<string, unknown>
        | undefined;

      currentInventory = updateMaxBookingForDate(
        currentInventory ?? {},
        Number(itemId),
        dateKey,
        changedFields[0]?.value,
        dependencies
      );

      const newEntry = currentInventory[dateKey]?.packs?.[Number(itemId)] as
        | Record<string, unknown>
        | undefined;

      const formFields = getChangedFormFields(
        prevEntry,
        newEntry,
        dateKey,
        itemId
      );

      setModifiedInventory(currentInventory);

      if (formFields.length > 0) {
        storeForm?.setFields(formFields);
      }
    },
    [
      rawInventory,
      dependencies,
      modifiedInventory,
      setModifiedInventory,
      storeForm,
    ]
  );

  if (isLoadingPacks) {
    return <PackTabSkeleton isDaytime={isDaytime} />;
  }

  return (
    <Form
      form={form}
      className={classNames(styles.packInventoryForm, {
        [styles.noPadding]: sortedPacks.length === 0,
      })}
      layout="vertical"
      onFinish={onFinish}
      initialValues={inventoryPacks}
      onFieldsChange={handleFieldsChange}
    >
      {sortedPacks.map((pack) =>
        isDaytime ? (
          <DayPackTableItem key={pack.id} pack={pack} />
        ) : (
          <PackTableItem
            key={pack.id}
            pack={pack}
            packsScore={packsScores?.[pack.id].packScore}
          />
        )
      )}

      {sortedPacks.length <= 1 && (
        <EmptyState
          title={isDaytime ? 'Create new day packages' : 'Create new packages'}
          subtitle="Generate more bookings with new packages."
          hasBorder={sortedPacks.length > 0}
        />
      )}
    </Form>
  );
};
